import React, { useCallback } from "react";

import { mdiTrashCanOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CUEPOINT_TIMECODE_REGEX } from "../../../constants/media";
import {
  blocksCuepoint,
  isValidTimecode,
  maskCuepoint,
} from "../../../utils/cuepoints";
import prettyDurations, {
  DURATION_UNITS,
  timecodeToDuration,
} from "../../../utils/durations";
import Input from "../../form/Input";
import Switch from "../../form/Switch";

import type { JSX } from "react";
import type { FieldArrayWithId } from "react-hook-form";

import type { CuepointsFormValues } from "../TabContentCuePoints";

interface CuepointsTableRowProps {
  index: number;
  cuepoint: FieldArrayWithId<CuepointsFormValues, "cuepoints">;
  mediaDurationMs: number | null;
  deleteCuepoint: (index: number) => void;
  onBlurTimecode: () => void;
}

function CuepointsTableRow({
  index,
  cuepoint,
  mediaDurationMs,
  deleteCuepoint,
  onBlurTimecode,
}: CuepointsTableRowProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const { getValues } = useFormContext<CuepointsFormValues>();

  const cuepointList = getValues().cuepoints;

  const validateTimecode = useCallback(
    (value: string): true | string => {
      if (!CUEPOINT_TIMECODE_REGEX.test(value)) {
        return t("invalidTimecodeFormat");
      }

      const currentTimecodeMs =
        timecodeToDuration(value).millisecondsWithFrames;

      const isDuplicate =
        cuepointList.filter(
          (cuepointListItem) =>
            isValidTimecode(cuepointListItem.timecode) &&
            timecodeToDuration(cuepointListItem.timecode)
              .millisecondsWithFrames === currentTimecodeMs,
        ).length > 1;

      if (isDuplicate) {
        return t("duplicateTimecode");
      }

      if (mediaDurationMs && currentTimecodeMs > mediaDurationMs) {
        const mediaDurationFormated = prettyDurations(mediaDurationMs, {
          valueUnit: DURATION_UNITS.MILLISECONDS,
        }).timecodeDuration;

        return t("durationExceedsMediaDuration", {
          mediaDuration: mediaDurationFormated,
        });
      }

      return true;
    },
    [cuepointList, mediaDurationMs, t],
  );

  return (
    <div className="border-border flex items-center justify-between gap-[0.46875rem] border-b px-[1.5625rem] py-[0.46875rem]">
      <div className="flex basis-1/3">
        <Switch
          key={cuepoint.id}
          className="!mb-0"
          name={`cuepoints.${index}.enabled`}
          size="sm"
        />
      </div>
      <div className="flex basis-1/3 justify-center">
        <Input
          key={cuepoint.id}
          isTooltipErrorMessage
          blocks={blocksCuepoint}
          className="!mb-0 w-[6.25rem]!"
          mask={maskCuepoint}
          name={`cuepoints.${index}.timecode`}
          onBlurCustom={onBlurTimecode}
          placeholder="00:00:00:00"
          validate={validateTimecode}
        />
      </div>
      <div className="mr-[0.3125rem] flex basis-1/3 justify-end">
        <button
          className="cursor-pointer"
          onClick={(): void => deleteCuepoint(index)}
          type="button"
        >
          <Icon color="#D92D20" path={mdiTrashCanOutline} size={0.9375} />
        </button>
      </div>
    </div>
  );
}

export default CuepointsTableRow;
